import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "product", "warehouse", "location", "quantityLabel", "quantity", "quantityContainer",
    "locationRow", "purchasePriceRow", "warehouseLabel", "storageModeLabel",
    "purchaseDocumented", "saleDocumented", "docStatusBlock"
  ]

  static values = {
    restricted: { type: Boolean, default: true }
  }

  connect() {
    this.filterProductOptionsOnLoad()
    this.updateQuantityLabel()
    this.toggleQuantityField()
    this.updateFieldVisibility()
    this.updateDocStatus()
  }

  parseProductValue() {
    // The select value is "product_id:location_id" to avoid Tom Select cloning issues
    // We parse it on form submit via beforeSubmit handler
  }

  beforeSubmit(event) {
    const selectedValue = this.productTarget.value

    if (selectedValue && selectedValue.includes(':')) {
      const [productId, locationId] = selectedValue.split(':')
      this.productTarget.value = productId
    }
  }

  toggleStorageMode() {
    this.restrictedValue = !this.restrictedValue
    this.filterProductOptions()
    this.updateFieldVisibility()
  }

  filterProductOptionsOnLoad() {
    // On initial load, filter product options to match current mode
    // without clearing the current selection (important for edit mode)
    const restricted = this.restrictedValue
    const currentValue = this.productTarget.value

    // Hide non-matching options in the underlying select
    Array.from(this.productTarget.querySelectorAll('optgroup')).forEach(group => {
      let hasVisible = false
      Array.from(group.querySelectorAll('option')).forEach(option => {
        const isRestricted = option.dataset.restrictedStorage === 'true'
        const matches = isRestricted === restricted
        // Keep the currently selected option visible regardless
        if (matches || option.value === currentValue) {
          option.hidden = false
          option.disabled = false
          if (matches) hasVisible = true
        } else {
          option.hidden = true
          option.disabled = true
        }
      })
      group.hidden = !hasVisible
      group.disabled = !hasVisible
    })

    // Rebuild Tom Select with filtered options
    const tomSelect = this.productTarget.tomselect
    if (tomSelect) {
      tomSelect.destroy()
      const comboboxController = this.application.getControllerForElementAndIdentifier(this.productTarget, 'combobox')
      if (comboboxController) {
        comboboxController.connect()
      }
    }
  }

  filterProductOptions() {
    const tomSelect = this.productTarget.tomselect
    if (tomSelect) {
      tomSelect.clear()
      tomSelect.destroy()
    }

    const restricted = this.restrictedValue
    // Show/hide options based on restricted_storage
    Array.from(this.productTarget.querySelectorAll('optgroup')).forEach(group => {
      let hasVisible = false
      Array.from(group.querySelectorAll('option')).forEach(option => {
        const isRestricted = option.dataset.restrictedStorage === 'true'
        if (isRestricted === restricted) {
          option.hidden = false
          option.disabled = false
          hasVisible = true
        } else {
          option.hidden = true
          option.disabled = true
        }
      })
      group.hidden = !hasVisible
      group.disabled = !hasVisible
    })

    // Also handle top-level options (not in optgroups)
    Array.from(this.productTarget.children).forEach(child => {
      if (child.tagName === 'OPTION' && child.value !== '') {
        const isRestricted = child.dataset.restrictedStorage === 'true'
        child.hidden = isRestricted !== restricted
        child.disabled = isRestricted !== restricted
      }
    })

    // Clear selection
    this.productTarget.value = ''

    // Re-initialize Tom Select via combobox controller
    const comboboxController = this.application.getControllerForElementAndIdentifier(this.productTarget, 'combobox')
    if (comboboxController) {
      comboboxController.connect()
    }
  }

  updateFieldVisibility() {
    const restricted = this.restrictedValue

    // Location row: visible only in restricted mode
    if (this.hasLocationRowTarget) {
      this.locationRowTarget.style.display = restricted ? '' : 'none'
    }

    // Purchase price row: visible only in unrestricted mode
    if (this.hasPurchasePriceRowTarget) {
      this.purchasePriceRowTarget.style.display = restricted ? 'none' : ''
    }

    // Warehouse label: "Терминал" or "Поставщик"
    if (this.hasWarehouseLabelTarget) {
      const label = this.warehouseLabelTarget
      label.textContent = restricted ? label.dataset.restrictedLabel : label.dataset.unrestrictedLabel
    }

    // Storage mode label text
    if (this.hasStorageModeLabelTarget) {
      const label = this.storageModeLabelTarget
      label.textContent = restricted ? label.dataset.restrictedLabel : label.dataset.unrestrictedLabel
    }
  }

  updateQuantityLabel() {
    const tomSelect = this.productTarget.tomselect
    const selectedValue = tomSelect ? tomSelect.getValue() : this.productTarget.value

    // Get the actual option element by composite value
    const selectedOption = Array.from(this.productTarget.options).find(opt => opt.value === selectedValue)

    if (!selectedOption) return

    const unitCode = selectedOption.dataset.unitCode

    if (unitCode) {
      this.quantityLabelTarget.textContent = `${this.quantityLabelTarget.dataset.originalLabel} (${unitCode})`
    } else {
      this.quantityLabelTarget.textContent = this.quantityLabelTarget.dataset.originalLabel
    }
  }

  toggleQuantityField() {
    const tomSelect = this.productTarget.tomselect
    const selectedValue = tomSelect ? tomSelect.getValue() : this.productTarget.value

    // Get the actual option element by composite value
    const selectedOption = Array.from(this.productTarget.options).find(opt => opt.value === selectedValue)

    if (!selectedOption || !this.hasQuantityContainerTarget) return

    const isBagged = selectedOption.dataset.isBagged === "true"

    if (isBagged) {
      this.quantityContainerTarget.style.display = ""
    } else {
      this.quantityContainerTarget.style.display = "none"
      // Clear the quantity value for bulk products
      if (this.hasQuantityTarget) {
        this.quantityTarget.value = ""
      }
    }
  }

  autoSelectWarehouse() {
    const tomSelect = this.productTarget.tomselect
    const selectedValue = tomSelect ? tomSelect.getValue() : this.productTarget.value

    // Get the actual option element by composite value
    const selectedOption = Array.from(this.productTarget.options).find(opt => opt.value === selectedValue)

    if (!selectedOption) return

    const warehouseId = selectedOption.dataset.warehouseId

    if (!warehouseId || !this.hasWarehouseTarget) return

    const warehouseSelect = this.warehouseTarget
    const warehouseTomSelect = warehouseSelect.tomselect

    if (warehouseTomSelect && warehouseTomSelect.getValue() !== warehouseId) {
      warehouseTomSelect.setValue(warehouseId, false)
      // Trigger dependent-select filter for locations and users
      warehouseSelect.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Auto-select location after warehouse is set
    this.autoSelectLocation()
  }

  autoSelectLocation() {
    const tomSelect = this.productTarget.tomselect
    const selectedValue = tomSelect ? tomSelect.getValue() : this.productTarget.value

    // Get the actual option element by composite value
    const selectedOption = Array.from(this.productTarget.options).find(opt => opt.value === selectedValue)

    if (!selectedOption) return

    const locationId = selectedOption.dataset.locationId

    if (!locationId || !this.hasLocationTarget) return

    const locationSelect = this.locationTarget
    const locationTomSelect = locationSelect.tomselect

    if (locationTomSelect && locationTomSelect.getValue() !== locationId) {
      // Use setTimeout to ensure the dependent-select filter has completed
      setTimeout(() => {
        locationTomSelect.setValue(locationId, false)
      }, 100)
    }
  }

  updateDocStatus() {
    if (!this.hasDocStatusBlockTarget) return

    const purchase = this.hasPurchaseDocumentedTarget && this.purchaseDocumentedTarget.checked
    const sale = this.hasSaleDocumentedTarget && this.saleDocumentedTarget.checked
    const block = this.docStatusBlockTarget

    let borderColor, bgColor, textColor, emoji, title, lines

    if (purchase && sale) {
      borderColor = "border-green-400"
      bgColor = "bg-green-50 dark:bg-green-900/20"
      textColor = "text-green-800 dark:text-green-300"
      emoji = "\u{1F7E2}"
      title = "Купили с доками — Продали с доками"
      lines = [
        "Полностью документированный вариант.",
        "НДС к зачёту принимается, КПН начисляется с чистой прибыли.",
        "Оптимально и безопасно для масштабирования."
      ]
    } else if (purchase && !sale) {
      borderColor = "border-yellow-400"
      bgColor = "bg-yellow-50 dark:bg-yellow-900/20"
      textColor = "text-yellow-800 dark:text-yellow-300"
      emoji = "\u{1F7E1}"
      title = "Купили с доками — Продали без доков"
      lines = [
        "Входящий НДС не принимается к зачёту и увеличивает себестоимость.",
        "Налоги не начисляются, но возникает разрыв оборота.",
        "Использовать с осторожностью."
      ]
    } else if (!purchase && sale) {
      borderColor = "border-red-400"
      bgColor = "bg-red-50 dark:bg-red-900/20"
      textColor = "text-red-800 dark:text-red-300"
      emoji = "\u{1F534}"
      title = "Купили без доков — Продали с доками"
      lines = [
        "Расход не признаётся.",
        "НДС и КПН начисляются со всей суммы продажи.",
        "Наиболее налогово затратный вариант."
      ]
    } else {
      borderColor = "border-gray-400"
      bgColor = "bg-gray-50 dark:bg-gray-800"
      textColor = "text-gray-800 dark:text-gray-300"
      emoji = "\u{26AB}"
      title = "Купили без доков — Продали без доков"
      lines = [
        "Налоги не начисляются.",
        "Оборот не отражается официально.",
        "Высокие риски при безналичных расчётах и масштабировании."
      ]
    }

    block.innerHTML = `
      <div class="rounded-md border-l-4 ${borderColor} ${bgColor} p-4">
        <p class="font-semibold ${textColor}">${emoji} ${title}</p>
        ${lines.map(l => `<p class="mt-1 text-sm ${textColor}">${l}</p>`).join("")}
      </div>
    `
  }

}
